import React from 'react';

import { useListing } from 'hooks';

import {
  ListingDetails,
  Photos,
  VirtualTour,
  Description,
  Location,
  OpenHouses,
  Exterior,
  CondoDetails,
  PropertyDetails,
  Rooms,
  Required,
  IncludedExcluded,
  MoreInfo,
  Financials,
  PublicAttachments,
} from '../components';
import {
  ListingFormState,
  PhotoFileType,
  AttachmentsType,
  UpdateFileType,
} from '../types';
import { useCards } from '../hooks';
import { StyledForm } from '../StyledComponents';

type DetailsFormProps = {
  listing?: ListingFormState;
  setListing: React.Dispatch<React.SetStateAction<ListingFormState>>;
  photos: PhotoFileType[];
  setPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  setDeletedPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  thumbnail: PhotoFileType | null;
  setThumbnail: React.Dispatch<React.SetStateAction<PhotoFileType | null>>;
  attachments: AttachmentsType;
  updateFile: UpdateFileType;
  errorMessage?: string;
  formFieldsMissing?: boolean;
  openHousesErrors: boolean[];
  editing?: boolean;
  uploading: boolean;
  hasNarSettlement?: boolean;
};

const DetailsForm: React.FC<DetailsFormProps> = ({
  listing,
  setListing,
  photos,
  setPhotos,
  setDeletedPhotos,
  thumbnail,
  setThumbnail,
  errorMessage,
  formFieldsMissing,
  openHousesErrors,
  attachments,
  updateFile,
  editing,
  uploading,
  hasNarSettlement,
}) => {
  const cards = useCards(listing!);

  return (
    <StyledForm>
      <ListingDetails
        editing={editing}
        errorMessage={errorMessage}
        formFieldsMissing={formFieldsMissing}
        type={listing?.type}
        subtype={listing?.subtype}
        line1={listing?.line1}
        line2={listing?.line2}
        country={listing?.country}
        province={listing?.province}
        city={listing?.city}
        neighborhood={listing?.neighborhood}
        postal_code={listing?.postal_code}
        list_price={listing?.list_price}
        list_price_type={listing?.list_price_type}
        expiry={listing?.expiry}
        commission={listing?.commission}
        status={listing?.status}
        coming_soon_date={listing?.coming_soon_date}
        reps={listing?.reps || []}
        setListing={setListing}
        hasNarSettlement={hasNarSettlement}
      />
      {cards.has('photos') && (
        <Photos
          uploading={uploading}
          photos={photos}
          setPhotos={setPhotos}
          setDeletedPhotos={setDeletedPhotos}
          thumbnail={thumbnail}
          setThumbnail={setThumbnail}
        />
      )}
      {cards.has('virtualTour') && (
        <VirtualTour
          virtualTourUrl={listing?.virtualTourUrl}
          setListing={setListing}
        />
      )}
      {cards.has('description') && (
        <Description
          client_remarks={listing?.client_remarks}
          brokerage_remarks={listing?.brokerage_remarks}
          setListing={setListing}
          hasNarSettlement={hasNarSettlement}
        />
      )}
      {cards.has('location') && (
        <Location
          subtype={listing?.subtype}
          dir_cross={listing?.dir_cross}
          fronts_on={listing?.fronts_on}
          taxes={listing?.taxes}
          possession={listing?.possession}
          occupancy={listing?.occupancy}
          legal_description={listing?.legal_description}
          setListing={setListing}
          holdover={listing?.holdover}
        />
      )}
      {cards.has('openHouses') && (
        <OpenHouses
          timezone={listing?.timezone}
          openHouses={listing?.openHouses || []}
          openHousesErrors={openHousesErrors}
          setListing={setListing}
        />
      )}
      {cards.has('exterior') && (
        <Exterior
          type={listing?.type}
          subtype={listing?.subtype}
          exteriorType={listing?.exteriorType}
          style={listing?.style}
          exterior={listing?.exterior}
          lot_front={listing?.lot_front}
          lot_depth={listing?.lot_depth}
          garage_type={listing?.garage_type}
          garage_spaces={listing?.garage_spaces}
          drive={listing?.drive}
          parking_spaces={listing?.parking_spaces}
          parking_cost={listing?.parking_cost}
          parking_included={listing?.parking_included}
          private_entrance={listing?.private_entrance}
          setListing={setListing}
        />
      )}
      {cards.has('moreInfo') && (
        <MoreInfo more_info={listing?.more_info} setListing={setListing} />
      )}
      {cards.has('condoDetails') && (
        <CondoDetails
          exterior={listing?.exterior}
          garage_type={listing?.garage_type}
          garage_spaces={listing?.garage_spaces}
          condo={listing?.condo}
          setListing={setListing}
        />
      )}
      {cards.has('financials') && (
        <Financials financials={listing?.financials} setListing={setListing} />
      )}
      {cards.has('propertyDetails') && (
        <PropertyDetails
          type={listing?.type}
          subtype={listing?.subtype}
          total_rooms={listing?.total_rooms}
          bedrooms={listing?.bedrooms}
          bathrooms={listing?.bathrooms}
          rooms_plus={listing?.rooms_plus}
          bedrooms_plus={listing?.bedrooms_plus}
          kitchens={listing?.kitchens}
          basement={listing?.basement}
          laundry_access={listing?.laundry_access}
          heat_source={listing?.heat_source}
          heat_type={listing?.heat_type}
          ac={listing?.ac}
          age={listing?.age}
          apx_sqft={listing?.apx_sqft}
          sqft={listing?.sqft}
          water={listing?.water}
          utilities={listing?.utilities}
          setListing={setListing}
        />
      )}
      {cards.has('required') && (
        <Required required={listing?.required} setListing={setListing} />
      )}
      {cards.has('includedExcluded') && (
        <IncludedExcluded
          freehold={listing?.freehold}
          setListing={setListing}
        />
      )}
      {cards.has('rooms') && (
        <Rooms
          rooms={listing?.rooms}
          setListing={setListing}
          roomUnit={listing?.roomUnit}
        />
      )}
      {cards.has('attachments') && (
        <PublicAttachments attachments={attachments} updateFile={updateFile} />
      )}
    </StyledForm>
  );
};

export default DetailsForm;
